import { useMutation, useQueryClient } from '@tanstack/react-query';

import { RequestsService } from '../services/requestsService';
import { Request, UpdateRequestData } from '../types';
import { offlineRequestsKeys } from './queries/useOfflineRequestsQuery';
import { useNetworkStatus } from './queries/useOfflineRequestsQuery';
import { requestsKeys } from './queries/useRequestsQuery';
import { offlineCache } from '../services/offlineCache';
import { RequestStage } from '../types/stages';

interface StageUpdateParams {
  requestId: string;
  requestData: UpdateRequestData;
  currentStage: RequestStage;
  newStage: RequestStage;
  /** #1456 — required by POST /submit */
  submitIntent?: 'submit_for_approval' | 'submit_to_vertiv' | 'approve';
}

export const useStageUpdate = () => {
  const queryClient = useQueryClient();
  const isOnline = useNetworkStatus();

  const performOptimisticUpdate = (
    requestId: string,
    newStage: RequestStage,
    submitIntent?: 'submit_for_approval' | 'submit_to_vertiv' | 'approve',
  ) => {
    if (submitIntent === 'approve') {
      return {
        previousDetailData: undefined,
        previousListData: undefined,
        previousOfflineDetailData: undefined,
        previousOfflineListData: undefined,
        requestDetailKey: requestsKeys.detail(requestId),
        requestsListKey: requestsKeys.lists(),
        offlineDetailKey: offlineRequestsKeys.detail(requestId),
        offlineListKey: offlineRequestsKeys.lists(),
      };
    }

    const requestDetailKey = requestsKeys.detail(requestId);
    const requestsListKey = requestsKeys.lists();
    const offlineDetailKey = offlineRequestsKeys.detail(requestId);
    const offlineListKey = offlineRequestsKeys.lists();

    const previousDetailData = queryClient.getQueryData(requestDetailKey);
    const previousListData = queryClient.getQueryData(requestsListKey);
    const previousOfflineDetailData =
      queryClient.getQueryData(offlineDetailKey);
    const previousOfflineListData = queryClient.getQueryData(offlineListKey);

    const patchRequest = (
      oldData: Request | undefined,
    ): Request | undefined => {
      if (!oldData) return oldData;
      if (submitIntent === 'submit_for_approval') {
        return {
          ...oldData,
          stage: RequestStage.AWAITING_APPROVAL,
          sysRequestStage: RequestStage.AWAITING_APPROVAL,
        };
      }
      return { ...oldData, stage: newStage, sysRequestStage: newStage };
    };

    queryClient.setQueryData(requestDetailKey, patchRequest);
    queryClient.setQueryData(offlineDetailKey, patchRequest);
    queryClient.setQueryData(
      requestsListKey,
      (oldData: Request[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(request =>
          request.id === requestId
            ? (patchRequest(request) as Request)
            : request,
        );
      },
    );
    queryClient.setQueryData(
      offlineListKey,
      (oldData: Request[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(request =>
          request.id === requestId
            ? (patchRequest(request) as Request)
            : request,
        );
      },
    );

    return {
      previousDetailData,
      previousListData,
      previousOfflineDetailData,
      previousOfflineListData,
      requestDetailKey,
      requestsListKey,
      offlineDetailKey,
      offlineListKey,
    };
  };

  const rollbackOptimisticUpdate = (rollbackData: any) => {
    const {
      previousDetailData,
      previousListData,
      previousOfflineDetailData,
      previousOfflineListData,
      requestDetailKey,
      requestsListKey,
      offlineDetailKey,
      offlineListKey,
    } = rollbackData;

    queryClient.setQueryData(requestDetailKey, previousDetailData);
    queryClient.setQueryData(requestsListKey, previousListData);
    queryClient.setQueryData(offlineDetailKey, previousOfflineDetailData);
    queryClient.setQueryData(offlineListKey, previousOfflineListData);
  };

  return useMutation({
    mutationFn: async ({
      requestId,
      requestData,
      currentStage,
      newStage,
      submitIntent = 'submit_to_vertiv',
    }: StageUpdateParams) => {
      // Block offline operations
      if (!isOnline) {
        throw new Error(
          'Cannot change request stages while offline. Please connect to internet.',
        );
      }

      // Perform optimistic update with animation trigger
      const rollbackData = performOptimisticUpdate(
        requestId,
        newStage,
        submitIntent,
      );

      try {
        const result = await RequestsService.submitRequest(
          requestId,
          requestData,
          submitIntent,
        );

        return { result, rollbackData };
      } catch (error) {
        rollbackOptimisticUpdate(rollbackData);
        throw error;
      }
    },
    onSuccess: async ({ result }) => {
      // Update with real server data
      const requestDetailKey = requestsKeys.detail(result.id);
      const requestsListKey = requestsKeys.lists();
      const offlineDetailKey = offlineRequestsKeys.detail(result.id);
      const offlineListKey = offlineRequestsKeys.lists();

      queryClient.setQueryData(requestDetailKey, result);
      queryClient.setQueryData(offlineDetailKey, result);

      queryClient.setQueryData(
        requestsListKey,
        (oldData: Request[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map(request =>
            request.id === result.id ? result : request,
          );
        },
      );

      queryClient.setQueryData(
        offlineListKey,
        (oldData: Request[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map(request =>
            request.id === result.id ? result : request,
          );
        },
      );

      // Also update the offline cache to prevent stale data issues
      try {
        const { requests } = await offlineCache.getCachedRequests();
        const updatedRequests = requests.map(req =>
          req.id === result.id ? result : req,
        );
        await offlineCache.cacheRequests(updatedRequests);

        // Verify cache write succeeded
        const { requests: verifyRequests } =
          await offlineCache.getCachedRequests();
        const cachedRequest = verifyRequests.find(req => req.id === result.id);
        if (!cachedRequest) {
          console.warn(
            'Offline cache write verification failed: request not found after update',
          );
        }
      } catch (error) {
        console.error('Failed to update offline cache:', error);
      }

      // Invalidate paginated queries to reflect changes in ViewRequests table
      queryClient.invalidateQueries({
        queryKey: requestsKeys.all,
        refetchType: 'active', // Only refetch queries that are currently being used
      });
    },
    onError: error => {
      console.error('Stage update failed:', error);
      // The rollback was already performed in the mutationFn
    },
    meta: {
      errorMessage: 'Failed to update request stage. Please try again.',
    },
  });
};
